import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { MainLayout } from '../components/layout/MainLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { ScrollArea } from '../components/ui/scroll-area';
import { toast } from 'sonner';
import { format, formatDistanceToNow } from 'date-fns';
import {
    Presentation,
    Target,
    Award,
    CheckCircle,
    Clock,
    TrendingUp,
    TrendingDown,
    Minus,
    Play,
    Eye,
    Loader2,
    RefreshCw,
    Filter,
    ArrowUpDown,
    CheckCircle2,
    XCircle,
    Star,
    MessageSquare,
    FileText,
    Video,
    Mic,
    Brain,
    Sparkles,
    BarChart3
} from 'lucide-react';

// Category configuration
const CATEGORIES = {
    content_clarity: { name: 'Content Clarity', icon: FileText, color: 'text-blue-500', bg: 'bg-blue-500/10' },
    structure_flow: { name: 'Structure & Flow', icon: BarChart3, color: 'text-purple-500', bg: 'bg-purple-500/10' },
    verbal_communication: { name: 'Verbal Communication', icon: Mic, color: 'text-emerald-500', bg: 'bg-emerald-500/10' },
    confidence_presence: { name: 'Confidence & Presence', icon: Star, color: 'text-amber-500', bg: 'bg-amber-500/10' },
    engagement_level: { name: 'Engagement', icon: Sparkles, color: 'text-pink-500', bg: 'bg-pink-500/10' },
    slide_effectiveness: { name: 'Slide Effectiveness', icon: Presentation, color: 'text-indigo-500', bg: 'bg-indigo-500/10' },
    qa_handling: { name: 'Q&A Handling', icon: MessageSquare, color: 'text-teal-500', bg: 'bg-teal-500/10' }
};

const EvolveAnalytics = () => {
    const { api } = useAuth();
    const navigate = useNavigate();
    
    const [loading, setLoading] = useState(true);
    const [sessions, setSessions] = useState([]);
    const [progressData, setProgressData] = useState(null);
    const [selectedSession, setSelectedSession] = useState(null);
    const [showDetailModal, setShowDetailModal] = useState(false);
    const [sortField, setSortField] = useState('created_at');
    const [sortOrder, setSortOrder] = useState('desc');
    const [filterStatus, setFilterStatus] = useState('all');
    const [filterGrade, setFilterGrade] = useState('all');
    const [analyzingSessionId, setAnalyzingSessionId] = useState(null);

    useEffect(() => {
        fetchData();
    }, []);

    const fetchData = async () => {
        setLoading(true);
        try {
            // Fetch sessions and progress history
            const [sessionsRes, progressRes] = await Promise.all([
                api.get('/evolve/sessions'),
                api.get('/evolve/sessions/user/progress-history?limit=50')
            ]);
            
            setSessions(sessionsRes.data || []);
            setProgressData(progressRes.data || null);
            
        } catch (error) {
            console.error('Failed to fetch evolve data:', error);
            toast.error('Failed to load presentation data');
        } finally {
            setLoading(false);
        }
    };

    // Retry analysis for sessions stuck in "completed" status
    const retryAnalysis = async (sessionId) => {
        setAnalyzingSessionId(sessionId);
        try {
            toast.info('Analyzing your presentation...', { duration: 3000 });
            const response = await api.post(`/evolve/sessions/${sessionId}/analyze`);
            
            // Update the session in state
            setSessions(prev => prev.map(s => 
                s.id === sessionId 
                    ? { ...s, status: 'analyzed', overall_score: response.data?.overall_score }
                    : s
            ));
            
            toast.success('Analysis complete!', {
                description: `Score: ${response.data?.overall_score || 'N/A'}%`
            });
            
            // Refresh data to get full updated session
            fetchData();
            
        } catch (error) {
            const errorDetail = error.response?.data?.detail;
            const errorMessage = typeof errorDetail === 'object' 
                ? errorDetail.message || errorDetail.recommendation
                : (errorDetail || 'Analysis failed');
            
            toast.error('Analysis could not be completed', {
                description: errorMessage,
                duration: 6000
            });
        } finally {
            setAnalyzingSessionId(null);
        }
    };

    const getScoreColor = (score) => {
        if (score >= 80) return 'text-emerald-600 dark:text-emerald-400';
        if (score >= 60) return 'text-amber-600 dark:text-amber-400';
        return 'text-red-600 dark:text-red-400';
    };

    const getGradeBadge = (grade) => {
        const gradeColors = {
            'A+': 'bg-emerald-500/20 text-emerald-700 dark:text-emerald-300',
            'A': 'bg-emerald-500/20 text-emerald-700 dark:text-emerald-300',
            'A-': 'bg-emerald-500/20 text-emerald-700 dark:text-emerald-300',
            'B+': 'bg-blue-500/20 text-blue-700 dark:text-blue-300',
            'B': 'bg-blue-500/20 text-blue-700 dark:text-blue-300',
            'B-': 'bg-blue-500/20 text-blue-700 dark:text-blue-300',
            'C+': 'bg-amber-500/20 text-amber-700 dark:text-amber-300',
            'C': 'bg-amber-500/20 text-amber-700 dark:text-amber-300',
            'C-': 'bg-amber-500/20 text-amber-700 dark:text-amber-300',
            'D': 'bg-red-500/20 text-red-700 dark:text-red-300',
            'F': 'bg-red-500/20 text-red-700 dark:text-red-300'
        };
        return <Badge className={gradeColors[grade] || 'bg-gray-500/20'}>{grade || '-'}</Badge>;
    };

    const getStatusBadge = (status) => {
        const statusConfig = {
            analyzed: { label: 'Completed', className: 'bg-emerald-500/20 text-emerald-700 dark:text-emerald-300' },
            completed: { label: 'Pending Analysis', className: 'bg-amber-500/20 text-amber-700 dark:text-amber-300' },
            in_progress: { label: 'In Progress', className: 'bg-blue-500/20 text-blue-700 dark:text-blue-300' },
            created: { label: 'Draft', className: 'bg-gray-500/20 text-gray-700 dark:text-gray-300' },
            processing: { label: 'Processing', className: 'bg-purple-500/20 text-purple-700 dark:text-purple-300' }
        };
        const config = statusConfig[status] || { label: status, className: 'bg-gray-500/20' };
        return <Badge className={config.className}>{config.label}</Badge>;
    };

    const getTrendIcon = (trend) => {
        if (trend === 'improving') return <TrendingUp className="h-4 w-4 text-emerald-500" />;
        if (trend === 'declining') return <TrendingDown className="h-4 w-4 text-red-500" />;
        return <Minus className="h-4 w-4 text-gray-400" />;
    };

    const viewSessionDetail = async (session) => {
        try {
            const response = await api.get(`/evolve/sessions/${session.id}/feedback`);
            setSelectedSession({
                ...session,
                feedback: response.data
            });
            setShowDetailModal(true);
        } catch (error) {
            setSelectedSession(session);
            setShowDetailModal(true);
        }
    };

    const formatCategoryName = (key) => {
        return CATEGORIES[key]?.name || key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
    };

    // Sort and filter
    const sortedSessions = [...sessions]
        .filter(s => filterStatus === 'all' || s.status === filterStatus)
        .filter(s => {
            if (filterGrade === 'all') return true;
            if (filterGrade === 'passing') return s.overall_score >= 80;
            return false;
        })
        .sort((a, b) => {
            let aVal, bVal;
            switch(sortField) {
                case 'score':
                    aVal = a.overall_score || 0;
                    bVal = b.overall_score || 0;
                    break;
                case 'status':
                    aVal = a.status || '';
                    bVal = b.status || '';
                    break;
                case 'topic':
                    aVal = a.topic_name || '';
                    bVal = b.topic_name || '';
                    break;
                default:
                    aVal = new Date(a.created_at || 0);
                    bVal = new Date(b.created_at || 0);
            }
            if (sortOrder === 'asc') return aVal > bVal ? 1 : -1;
            return aVal < bVal ? 1 : -1;
        });

    const toggleSort = (field) => {
        if (sortField === field) {
            setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
        } else {
            setSortField(field);
            setSortOrder('desc');
        }
    };

    // Calculate summary stats
    const analyzedSessions = sessions.filter(s => s.status === 'analyzed');
    const passingCount = analyzedSessions.filter(s => (s.overall_score || 0) >= 80).length;
    const summary = progressData?.summary || {};

    if (loading) {
        return (
            <MainLayout>
                <div className="flex items-center justify-center h-[60vh]">
                    <Loader2 className="h-12 w-12 animate-spin text-primary" />
                </div>
            </MainLayout>
        );
    }

    return (
        <MainLayout>
            <div className="space-y-6 animate-fade-in" data-testid="evolve-analytics">
                {/* Header */}
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                    <div>
                        <h1 className="text-2xl md:text-3xl font-heading font-bold tracking-tight flex items-center gap-3">
                            <Video className="h-7 w-7 text-primary" />
                            Presentation Analytics
                        </h1>
                        <p className="text-muted-foreground mt-1">
                            Track your presentation coaching progress and performance
                        </p>
                    </div>
                    
                    <div className="flex items-center gap-2">
                        <Button variant="outline" size="sm" onClick={fetchData}>
                            <RefreshCw className="h-4 w-4 mr-2" />
                            Refresh
                        </Button>
                        <Button onClick={() => navigate('/evolve/new')}>
                            <Play className="h-4 w-4 mr-2" />
                            New Presentation
                        </Button>
                    </div>
                </div>

                {/* Summary Stats */}
                <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
                    <Card>
                        <CardContent className="p-4 text-center">
                            <p className="text-3xl font-bold">{sessions.length}</p>
                            <p className="text-xs text-muted-foreground">Total Sessions</p>
                        </CardContent>
                    </Card>
                    <Card>
                        <CardContent className="p-4 text-center">
                            <p className="text-3xl font-bold">{analyzedSessions.length}</p>
                            <p className="text-xs text-muted-foreground">Analyzed</p>
                        </CardContent>
                    </Card>
                    <Card className="border-emerald-500/30 bg-emerald-500/5">
                        <CardContent className="p-4 text-center">
                            <p className="text-3xl font-bold text-emerald-600 dark:text-emerald-400">{passingCount}</p>
                            <p className="text-xs text-emerald-600 dark:text-emerald-400 font-medium">Passed (80+)</p>
                        </CardContent>
                    </Card>
                    <Card>
                        <CardContent className="p-4 text-center">
                            <p className="text-3xl font-bold">{Math.round(summary.average_score || 0)}%</p>
                            <p className="text-xs text-muted-foreground">Avg Score</p>
                        </CardContent>
                    </Card>
                    <Card>
                        <CardContent className="p-4 text-center">
                            <p className="text-3xl font-bold">{summary.best_score || 0}%</p>
                            <p className="text-xs text-muted-foreground">Best Score</p>
                        </CardContent>
                    </Card>
                    <Card>
                        <CardContent className="p-4 text-center">
                            <div className="flex items-center justify-center gap-1">
                                {getTrendIcon(summary.trend)}
                                <span className="text-xl font-bold">
                                    {summary.trend_change > 0 ? '+' : ''}{summary.trend_change || 0}%
                                </span>
                            </div>
                            <p className="text-xs text-muted-foreground">Trend</p>
                        </CardContent>
                    </Card>
                </div>

                {/* Category Performance */}
                {summary.category_averages && Object.keys(summary.category_averages).some(k => summary.category_averages[k]) && (
                    <Card>
                        <CardHeader className="pb-3">
                            <CardTitle className="text-lg flex items-center gap-2">
                                <Target className="h-5 w-5 text-primary" />
                                Performance by Category
                            </CardTitle>
                            <CardDescription>
                                {summary.strongest_category && (
                                    <span className="text-emerald-600 dark:text-emerald-400">
                                        Strongest: {formatCategoryName(summary.strongest_category)}
                                    </span>
                                )}
                                {summary.weakest_category && summary.strongest_category && ' • '}
                                {summary.weakest_category && (
                                    <span className="text-amber-600 dark:text-amber-400">
                                        Focus area: {formatCategoryName(summary.weakest_category)}
                                    </span>
                                )}
                            </CardDescription>
                        </CardHeader>
                        <CardContent>
                            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3">
                                {Object.entries(summary.category_averages).map(([category, score]) => {
                                    if (score === null) return null;
                                    const config = CATEGORIES[category] || {};
                                    const Icon = config.icon || Target;
                                    const isStrong = category === summary.strongest_category;
                                    const isWeak = category === summary.weakest_category;
                                    
                                    return (
                                        <Card 
                                            key={category} 
                                            className={`
                                                ${isStrong ? 'border-emerald-500/50 bg-emerald-500/5' : ''}
                                                ${isWeak ? 'border-amber-500/50 bg-amber-500/5' : ''}
                                            `}
                                        >
                                            <CardContent className="p-3">
                                                <div className="flex items-center gap-2 mb-2">
                                                    <div className={`p-1.5 rounded ${config.bg || 'bg-gray-100'}`}>
                                                        <Icon className={`h-3 w-3 ${config.color || 'text-gray-500'}`} />
                                                    </div>
                                                    {isStrong && <Star className="h-3 w-3 text-emerald-500" />}
                                                </div>
                                                <p className="text-xs font-medium truncate">{config.name || category}</p>
                                                <p className={`text-lg font-bold mt-1 ${getScoreColor(score)}`}>
                                                    {Math.round(score)}%
                                                </p>
                                            </CardContent>
                                        </Card>
                                    );
                                })}
                            </div>
                        </CardContent>
                    </Card>
                )}

                {/* Filters */}
                <div className="flex flex-wrap items-center gap-3">
                    <div className="flex items-center gap-2">
                        <Filter className="h-4 w-4 text-muted-foreground" />
                        <span className="text-sm text-muted-foreground">Filter:</span>
                    </div>
                    <Select value={filterStatus} onValueChange={setFilterStatus}>
                        <SelectTrigger className="w-[150px]">
                            <SelectValue placeholder="Status" />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="all">All Status</SelectItem>
                            <SelectItem value="analyzed">Analyzed</SelectItem>
                            <SelectItem value="completed">Completed</SelectItem>
                            <SelectItem value="in_progress">In Progress</SelectItem>
                            <SelectItem value="created">Draft</SelectItem>
                        </SelectContent>
                    </Select>
                    <Select value={filterGrade} onValueChange={setFilterGrade}>
                        <SelectTrigger className="w-[150px]">
                            <SelectValue placeholder="Score" />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="all">All Scores</SelectItem>
                            <SelectItem value="passing">Passing (80+)</SelectItem>
                        </SelectContent>
                    </Select>
                    <span className="text-sm text-muted-foreground ml-auto">
                        Showing {sortedSessions.length} of {sessions.length} sessions
                    </span>
                </div>

                {/* Sessions Table */}
                <Card>
                    <CardContent className="p-0">
                        <ScrollArea className="h-[500px]">
                            <Table>
                                <TableHeader className="sticky top-0 bg-background">
                                    <TableRow>
                                        <TableHead className="w-[250px]">
                                            <Button variant="ghost" size="sm" onClick={() => toggleSort('topic')} className="h-8 px-2">
                                                Topic
                                                <ArrowUpDown className="ml-1 h-3 w-3" />
                                            </Button>
                                        </TableHead>
                                        <TableHead>
                                            <Button variant="ghost" size="sm" onClick={() => toggleSort('status')} className="h-8 px-2">
                                                Status
                                                <ArrowUpDown className="ml-1 h-3 w-3" />
                                            </Button>
                                        </TableHead>
                                        <TableHead>
                                            <Button variant="ghost" size="sm" onClick={() => toggleSort('score')} className="h-8 px-2">
                                                Score
                                                <ArrowUpDown className="ml-1 h-3 w-3" />
                                            </Button>
                                        </TableHead>
                                        <TableHead>Grade</TableHead>
                                        <TableHead>Slides</TableHead>
                                        <TableHead>
                                            <Button variant="ghost" size="sm" onClick={() => toggleSort('created_at')} className="h-8 px-2">
                                                Date
                                                <ArrowUpDown className="ml-1 h-3 w-3" />
                                            </Button>
                                        </TableHead>
                                        <TableHead className="text-right">Actions</TableHead>
                                    </TableRow>
                                </TableHeader>
                                <TableBody>
                                    {sortedSessions.length === 0 ? (
                                        <TableRow>
                                            <TableCell colSpan={7} className="text-center py-8 text-muted-foreground">
                                                {sessions.length === 0 
                                                    ? "No presentations yet. Create your first one!"
                                                    : "No sessions match your filters"
                                                }
                                            </TableCell>
                                        </TableRow>
                                    ) : (
                                        sortedSessions.map((session) => {
                                            const score = session.overall_score || 0;
                                            
                                            return (
                                                <TableRow 
                                                    key={session.id} 
                                                    className="cursor-pointer hover:bg-muted/50"
                                                    onClick={() => viewSessionDetail(session)}
                                                >
                                                    <TableCell>
                                                        <div className="flex items-center gap-2">
                                                            <div className="p-1.5 rounded bg-primary/10">
                                                                <Presentation className="h-3 w-3 text-primary" />
                                                            </div>
                                                            <span className="text-sm font-medium truncate max-w-[180px]">
                                                                {session.topic_name || 'Untitled Presentation'}
                                                            </span>
                                                        </div>
                                                    </TableCell>
                                                    <TableCell>{getStatusBadge(session.status)}</TableCell>
                                                    <TableCell>
                                                        <span className={`font-bold ${getScoreColor(score)}`}>
                                                            {score > 0 ? `${Math.round(score)}%` : '-'}
                                                        </span>
                                                    </TableCell>
                                                    <TableCell>
                                                        {session.grade ? getGradeBadge(session.grade) : '-'}
                                                    </TableCell>
                                                    <TableCell>
                                                        <span className="text-sm">{session.slide_count || 0}</span>
                                                    </TableCell>
                                                    <TableCell>
                                                        <span className="text-sm text-muted-foreground">
                                                            {session.created_at 
                                                                ? formatDistanceToNow(new Date(session.created_at), { addSuffix: true })
                                                                : '-'
                                                            }
                                                        </span>
                                                    </TableCell>
                                                    <TableCell className="text-right">
                                                        <div className="flex items-center justify-end gap-2">
                                                            {session.status === 'completed' && (
                                                                <Button 
                                                                    variant="outline" 
                                                                    size="sm"
                                                                    disabled={analyzingSessionId === session.id}
                                                                    onClick={(e) => {
                                                                        e.stopPropagation();
                                                                        retryAnalysis(session.id);
                                                                    }}
                                                                    data-testid="retry-analysis-btn"
                                                                >
                                                                    {analyzingSessionId === session.id ? (
                                                                        <Loader2 className="h-4 w-4 animate-spin" />
                                                                    ) : (
                                                                        <RefreshCw className="h-4 w-4" />
                                                                    )}
                                                                </Button>
                                                            )}
                                                            <Button 
                                                                variant="ghost" 
                                                                size="sm"
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    navigate(`/evolve/session/${session.id}`);
                                                                }}
                                                            >
                                                                <Eye className="h-4 w-4" />
                                                            </Button>
                                                        </div>
                                                    </TableCell>
                                                </TableRow>
                                            );
                                        })
                                    )}
                                </TableBody>
                            </Table>
                        </ScrollArea>
                    </CardContent>
                </Card>

                {/* Detail Modal */}
                <Dialog open={showDetailModal} onOpenChange={setShowDetailModal}>
                    <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
                        <DialogHeader>
                            <DialogTitle className="flex items-center gap-2">
                                <div className="p-2 rounded bg-primary/10">
                                    <Presentation className="h-5 w-5 text-primary" />
                                </div>
                                {selectedSession?.topic_name || 'Presentation Details'}
                            </DialogTitle>
                            <DialogDescription>
                                {selectedSession?.created_at && format(new Date(selectedSession.created_at), 'PPpp')}
                            </DialogDescription>
                        </DialogHeader>
                        
                        {selectedSession && (
                            <div className="space-y-4">
                                {/* Scores */}
                                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                                    <Card>
                                        <CardContent className="p-3 text-center">
                                            <p className={`text-2xl font-bold ${getScoreColor(selectedSession.overall_score || 0)}`}>
                                                {Math.round(selectedSession.overall_score || 0)}%
                                            </p>
                                            <p className="text-xs text-muted-foreground">Overall</p>
                                        </CardContent>
                                    </Card>
                                    <Card>
                                        <CardContent className="p-3 text-center">
                                            {selectedSession.grade ? getGradeBadge(selectedSession.grade) : <span className="text-2xl font-bold">-</span>}
                                            <p className="text-xs text-muted-foreground mt-1">Grade</p>
                                        </CardContent>
                                    </Card>
                                    <Card>
                                        <CardContent className="p-3 text-center">
                                            <p className="text-2xl font-bold">{selectedSession.slide_count || 0}</p>
                                            <p className="text-xs text-muted-foreground">Slides</p>
                                        </CardContent>
                                    </Card>
                                    <Card>
                                        <CardContent className="p-3 text-center">
                                            <p className="text-2xl font-bold">{selectedSession.qa_count || 0}</p>
                                            <p className="text-xs text-muted-foreground">Q&A</p>
                                        </CardContent>
                                    </Card>
                                </div>
                                
                                {/* Category Scores from Feedback */}
                                {selectedSession.feedback && (
                                    <div className="p-3 rounded-lg bg-muted/50">
                                        <h4 className="font-medium mb-3 flex items-center gap-2">
                                            <BarChart3 className="h-4 w-4 text-primary" />
                                            Category Scores
                                        </h4>
                                        <div className="grid grid-cols-2 gap-2">
                                            {Object.entries(CATEGORIES).map(([key, config]) => {
                                                const score = selectedSession.feedback[key];
                                                if (score === null || score === undefined) return null;
                                                const Icon = config.icon;
                                                return (
                                                    <div key={key} className="flex items-center justify-between p-2 bg-background rounded">
                                                        <div className="flex items-center gap-2">
                                                            <Icon className={`h-4 w-4 ${config.color}`} />
                                                            <span className="text-sm">{config.name}</span>
                                                        </div>
                                                        <span className={`font-bold ${getScoreColor(score)}`}>{Math.round(score)}%</span>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    </div>
                                )}
                                
                                {/* Details */}
                                <div className="grid grid-cols-2 gap-3 text-sm">
                                    <div className="flex justify-between p-2 bg-muted/50 rounded">
                                        <span className="text-muted-foreground">Status</span>
                                        {getStatusBadge(selectedSession.status)}
                                    </div>
                                    <div className="flex justify-between p-2 bg-muted/50 rounded">
                                        <span className="text-muted-foreground">Duration</span>
                                        <span>{selectedSession.duration_minutes || '-'} min</span>
                                    </div>
                                </div>
                                
                                {/* Actions */}
                                <div className="flex gap-2 pt-2">
                                    <Button 
                                        variant="outline" 
                                        className="flex-1"
                                        onClick={() => setShowDetailModal(false)}
                                    >
                                        Close
                                    </Button>
                                    <Button 
                                        className="flex-1"
                                        onClick={() => {
                                            setShowDetailModal(false);
                                            navigate(`/evolve/session/${selectedSession.id}`);
                                        }}
                                    >
                                        <Eye className="h-4 w-4 mr-2" />
                                        View Full Feedback
                                    </Button>
                                </div>
                            </div>
                        )}
                    </DialogContent>
                </Dialog>
            </div>
        </MainLayout>
    );
};

export default EvolveAnalytics;
